/**
 * Ordenação e paginação da tabela de dados.
 *
 * Funções puras, fora do componente: a regra de ordenação é o que faz a coluna
 * "tok/s (p95)" colocar o pior cenário no topo, e isso precisa ser verificável sem
 * desenhar tabela nenhuma.
 */

/** Sentido da ordenação. */
export type SortDirection = "asc" | "desc";

/** Estado de ordenação: qual coluna e em que sentido. */
export interface SortState {
  readonly key: string;
  readonly direction: SortDirection;
}

/** Valor pelo qual uma linha é ordenada numa coluna. */
export type SortValue<T> = (row: T) => number | string;

/** Colação usada para texto: acentos e caixa ordenados como o leitor espera em pt-BR. */
const TEXT_COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/**
 * Ordena uma cópia das linhas pelo valor da coluna.
 *
 * Nunca reordena a coleção recebida: a mesma lista alimenta outras partes da tela, e
 * reordenar no lugar mudaria o que elas mostram sem ninguém ter pedido. A ordenação é
 * estável — empates mantêm a ordem original, para a tabela não "piscar" ao clicar.
 */
export function sortRows<T>(
  rows: readonly T[],
  sortValue: SortValue<T> | undefined,
  direction: SortDirection,
): T[] {
  const copy = [...rows];
  if (sortValue === undefined) {
    return copy;
  }
  const factor = direction === "asc" ? 1 : -1;
  return copy
    .map((row, index) => ({ row, index, value: sortValue(row) }))
    .sort((a, b) => {
      const compared =
        typeof a.value === "number" && typeof b.value === "number"
          ? a.value - b.value
          : TEXT_COLLATOR.compare(String(a.value), String(b.value));
      return compared === 0 ? a.index - b.index : compared * factor;
    })
    .map((entry) => entry.row);
}

/**
 * Próximo estado de ordenação ao clicar numa coluna.
 *
 * Ciclo de três passos — crescente, decrescente, sem ordenação — para que sempre dê para
 * voltar à ordem original, que é a ordem em que o dado foi gerado.
 */
export function nextSort(current: SortState | null, key: string): SortState | null {
  if (current === null || current.key !== key) {
    return { key, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { key, direction: "desc" };
  }
  return null;
}

/** Quantidade de páginas para o total pedido; nunca menos que uma, nem com lista vazia. */
export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * Linhas da página pedida, com a página limitada ao intervalo válido.
 *
 * Página fora do intervalo cai na última existente em vez de devolver vazio: ao filtrar
 * uma tabela que estava na página 5, a lista encolhe e a tela não pode ficar em branco.
 */
export function pageSlice<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  if (pageSize <= 0) {
    return [...rows];
  }
  const last = pageCount(rows.length, pageSize);
  const safePage = Math.min(Math.max(1, Math.floor(page)), last);
  const start = (safePage - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}
